import { useEffect, useRef, useState, type JSX } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { BLOOD_TYPES, MBTI_DESCRIPTIONS } from '@dxtech/shared';
import { call } from '../api/library.js';
import { Avatar, Spinner } from '../ui/common.js';

// 임직원 통계 — 사는 지역(시/군/구 단위, 집 주소 아님) 지도 + MBTI·혈액형 분포. 전 임직원 열람
interface Region { region: string; people: Array<{ name: string; upn: string }>; lat?: number; lon?: number }
interface PeopleStatsData { total: number; regions: Region[]; noRegion: number; mbti: Record<string, number>; blood: Record<string, number> }

const BLUE = '#2563EB';
const LETTER: Record<string, string> = { E: '외향', I: '내향', S: '감각', N: '직관', T: '사고', F: '감정', J: '판단', P: '인식' };
const fetchStats = async () => (await (await call('/people-stats')).json()) as PeopleStatsData;

export function PeopleStats(): JSX.Element {
  const q = useQuery({ queryKey: ['people-stats'], queryFn: fetchStats, staleTime: 60_000 });
  if (q.isLoading) return <div style={{ padding: 60, textAlign: 'center' }}><Spinner /></div>;
  if (q.isError || !q.data) return <div className="dx-card" style={{ padding: 30, color: '#B91C1C' }}>통계를 불러오지 못했습니다.</div>;
  const d = q.data;
  const mbtiTotal = Object.values(d.mbti).reduce((a, b) => a + b, 0);
  const bloodTotal = Object.values(d.blood).reduce((a, b) => a + b, 0);
  const mbtiRows = Object.keys(MBTI_DESCRIPTIONS).map((t) => ({ k: t, n: d.mbti[t] ?? 0, hint: MBTI_DESCRIPTIONS[t] }))
    .sort((a, b) => b.n - a.n || a.k.localeCompare(b.k));
  // 축별 비율 — E/I, S/N, T/F, J/P
  const axes = [['E', 'I'], ['S', 'N'], ['T', 'F'], ['J', 'P']].map(([a, b], i) => {
    const na = Object.entries(d.mbti).filter(([t]) => t[i] === a).reduce((s, [, n]) => s + n, 0);
    return { a: a!, b: b!, na, nb: mbtiTotal - na };
  });

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', display: 'grid', gap: 16, animation: 'dxfade .3s ease' }}>
      <section className="dx-card" style={{ padding: 18 }}>
        <Head title="임직원 사는 곳" note={`재직 ${d.total}명 · 시/군/구 단위로만 표시${d.noRegion ? ` · 주소 미등록 ${d.noRegion}명` : ''}`} />
        <RegionMap regions={d.regions} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
          {[...d.regions].sort((a, b) => b.people.length - a.people.length).map((r) => (
            <span key={r.region} title={r.people.map((p) => p.name).join(', ')} style={{ fontSize: 12, color: '#374151', background: '#F3F4F6', borderRadius: 999, padding: '3px 10px' }}>
              {r.region.replace(/^(서울특별시|경기도|인천광역시) /, '')} <b className="tnum">{r.people.length}</b>
            </span>
          ))}
        </div>
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
        <section className="dx-card" style={{ padding: 18 }}>
          <Head title="MBTI" note={`입력 ${mbtiTotal}명 / ${d.total}명`} />
          {mbtiTotal === 0 ? <Empty /> : (
            <>
              <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
                {axes.map((x) => (
                  <div key={x.a} style={{ display: 'grid', gridTemplateColumns: '64px 1fr 64px', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                    <span style={{ textAlign: 'right', whiteSpace: 'nowrap' }}><span style={{ fontSize: 11.5, color: '#6B7280' }}>{LETTER[x.a]}</span> <b>{x.a}</b></span>
                    <div title={`${x.a}(${LETTER[x.a]}) ${x.na}명 · ${x.b}(${LETTER[x.b]}) ${x.nb}명`} style={{ display: 'flex', height: 18, borderRadius: 4, overflow: 'hidden', gap: 2, background: '#fff' }}>
                      {x.na > 0 && <div style={{ flex: x.na, background: BLUE, color: '#fff', fontSize: 11, paddingLeft: 6, lineHeight: '18px' }}>{x.na}</div>}
                      {x.nb > 0 && <div style={{ flex: x.nb, background: '#BFD3FB', color: '#1E3A8A', fontSize: 11, paddingRight: 6, textAlign: 'right', lineHeight: '18px' }}>{x.nb}</div>}
                    </div>
                    <span style={{ whiteSpace: 'nowrap' }}><b>{x.b}</b> <span style={{ fontSize: 11.5, color: '#6B7280' }}>{LETTER[x.b]}</span></span>
                  </div>
                ))}
              </div>
              <Bars rows={mbtiRows.filter((r) => r.n > 0)} total={mbtiTotal} />
            </>
          )}
        </section>

        <section className="dx-card" style={{ padding: 18 }}>
          <Head title="혈액형" note={`입력 ${bloodTotal}명 / ${d.total}명`} />
          {bloodTotal === 0 ? <Empty /> : <Bars rows={BLOOD_TYPES.map((t) => ({ k: `${t}형`, n: d.blood[t] ?? 0 }))} total={bloodTotal} />}
        </section>
      </div>
      <div style={{ fontSize: 11.5, color: '#9CA3AF' }}>MBTI·혈액형은 각자 내 정보 또는 프로파일에서 입력한 값입니다. 사는 곳은 임직원 정보의 주소에서 시/군/구만 읽어 표시합니다.</div>
    </div>
  );
}

function Head({ title, note }: { title: string; note: string }): JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
      <div style={{ fontSize: 15, fontWeight: 700 }}>{title}</div>
      <span style={{ fontSize: 12, color: '#9CA3AF' }}>{note}</span>
    </div>
  );
}

const Empty = (): JSX.Element => <div style={{ padding: 24, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>아직 입력한 사람이 없습니다</div>;

/** 가로 막대 — 단일 계열이라 한 색, 값·비율은 막대 오른쪽 텍스트로 */
function Bars({ rows, total }: { rows: Array<{ k: string; n: number; hint?: string }>; total: number }): JSX.Element {
  const max = Math.max(1, ...rows.map((r) => r.n));
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {rows.map((r) => (
        <div key={r.k} title={`${r.k}${r.hint ? ` · ${r.hint}` : ''} — ${r.n}명`} style={{ display: 'grid', gridTemplateColumns: '48px 1fr 72px', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
          <span style={{ fontWeight: 600, color: '#374151' }}>{r.k}</span>
          <div style={{ height: 14, background: '#F3F4F6', borderRadius: 4 }}>
            {r.n > 0 && <div style={{ width: `${(r.n / max) * 100}%`, height: '100%', background: BLUE, borderRadius: 4 }} />}
          </div>
          <span className="tnum" style={{ color: '#4B5563' }}>{r.n}명 <span style={{ color: '#9CA3AF' }}>{Math.round((r.n / total) * 100)}%</span></span>
        </div>
      ))}
    </div>
  );
}

const PIN = 34; // 프로필 사진 지름
const PIN_STEP = 22; // 같은 지역 사진끼리 겹쳐 놓는 간격
const PIN_MAX = 4; // 그 이상은 +N

/** OpenStreetMap 지도 — 지역마다 그 지역 임직원의 프로필 사진을 겹쳐 표시(4명 넘으면 +N), 클릭하면 이름 */
function RegionMap({ regions }: { regions: Region[] }): JSX.Element {
  const el = useRef<HTMLDivElement>(null);
  // Leaflet 마커 DOM 안에 React 아바타를 포털로 그림(사진은 인증 필요한 /api/photo 라 Avatar 의 조회·캐시를 그대로 씀)
  const [pins, setPins] = useState<Array<{ node: HTMLElement; r: Region }>>([]);
  useEffect(() => {
    if (!el.current) return;
    const map = L.map(el.current, { scrollWheelZoom: false }).setView([37.45, 126.95], 9);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 18, attribution: '&copy; OpenStreetMap contributors' }).addTo(map);
    const pts: L.LatLngExpression[] = [];
    const made: Array<{ node: HTMLElement; r: Region }> = [];
    for (const r of regions) {
      if (r.lat == null || r.lon == null) continue;
      pts.push([r.lat, r.lon]);
      const k = Math.min(r.people.length, PIN_MAX) + (r.people.length > PIN_MAX ? 1 : 0);
      const w = PIN + (k - 1) * PIN_STEP + 4;
      const names = r.people.map((p) => p.name.replace(/[<>&]/g, '')).join(', ');
      const m = L.marker([r.lat, r.lon], { icon: L.divIcon({ className: '', html: '', iconSize: [w, PIN + 4], iconAnchor: [w / 2, (PIN + 4) / 2] }), riseOnHover: true })
        .bindTooltip(`${r.region.split(' ').slice(1).join(' ') || r.region} · ${names}`, { direction: 'top', offset: [0, -(PIN / 2)] })
        .bindPopup(`<b>${r.region}</b><br/>${names}`)
        .addTo(map);
      const node = m.getElement();
      if (node) made.push({ node, r });
    }
    setPins(made);
    if (pts.length) map.fitBounds(L.latLngBounds(pts), { padding: [40, 40], maxZoom: 11 });
    return () => { setPins([]); map.remove(); };
  }, [regions]);
  return (
    <>
      <div ref={el} style={{ height: 420, borderRadius: 8, overflow: 'hidden', border: '1px solid #EDEFF2', zIndex: 0, position: 'relative' }} />
      {pins.map(({ node, r }) => createPortal(<PinStack r={r} />, node, r.region))}
    </>
  );
}

function PinStack({ r }: { r: Region }): JSX.Element {
  const shown = r.people.slice(0, PIN_MAX);
  const more = r.people.length - shown.length;
  const ring = { border: '2px solid #fff', borderRadius: '50%', boxShadow: '0 1px 4px rgba(16,24,40,.35)', lineHeight: 0, background: '#fff' } as const;
  return (
    <div style={{ display: 'flex', cursor: 'pointer' }}>
      {shown.map((p, i) => (
        <span key={p.upn} style={{ ...ring, marginLeft: i ? PIN_STEP - PIN : 0, zIndex: PIN_MAX - i }}>
          <Avatar name={p.name} upn={p.upn} size={PIN} index={i} />
        </span>
      ))}
      {more > 0 && (
        <span style={{ ...ring, marginLeft: PIN_STEP - PIN, width: PIN, height: PIN, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          background: BLUE, color: '#fff', fontSize: 12, fontWeight: 700, lineHeight: 1 }}>+{more}</span>
      )}
    </div>
  );
}
